import Link from '@tiptap/extension-link';
import {
  Fragment,
  Slice,
  type MarkType,
  type Node as ProseMirrorNode,
} from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Mapping } from '@tiptap/pm/transform';
import { find } from 'linkifyjs';

import { RICH_HTML_LINK_COLOR } from './richHtmlVisualContract';

import type { Editor } from '@tiptap/core';
import type { EditorState, Transaction } from '@tiptap/pm/state';

const AUTO_LINK_PLUGIN_KEY = new PluginKey('siteScopeCaretLeaveAutoLink');
const SYNC_EDITED_AUTO_LINK_KEY = new PluginKey('siteScopeSyncEditedAutoLink');

/** Protocol stored on `www.` matches. Toolbar rules only allow https and mailto. */
export const RICH_TEXT_AUTO_LINK_PROTOCOL = 'https';

export type RichTextAutoLink = {
  href: string;
};

/** `http://`, `javascript:`, and any scheme other than https or mailto. */
function hasDisallowedProtocol(value: string): boolean {
  return (
    /^[a-z][a-z0-9+.-]*:/i.test(value) &&
    !/^https:\/\//i.test(value) &&
    !/^mailto:/i.test(value)
  );
}

/**
 * Whole-token match for auto-link.
 * `https://` and `mailto:` are stored as typed.
 * `www.example.com` and `example.com` are stored as `https://…`.
 * Bare emails stay plain text.
 */
export function autoLinkMatch(value: string): RichTextAutoLink | null {
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed) || hasDisallowedProtocol(trimmed)) {
    return null;
  }
  if (/@/.test(trimmed) && !/^mailto:/i.test(trimmed)) {
    return null;
  }
  const match = find(trimmed, {
    defaultProtocol: RICH_TEXT_AUTO_LINK_PROTOCOL,
  }).find(
    token => token.isLink && token.start === 0 && token.end === trimmed.length,
  );
  if (!match) {
    return null;
  }
  if (/^mailto:/i.test(trimmed)) {
    return /^mailto:/i.test(match.href) ? { href: match.href } : null;
  }
  if (!/^https:\/\//i.test(match.href)) {
    return null;
  }
  if (!/^https:\/\//i.test(trimmed)) {
    const host = trimmed.split(/[/?#]/)[0] ?? '';
    if (!host.includes('.')) {
      return null;
    }
    // linkify treats some filename suffixes as TLDs. README.md and script.sh
    // stay plain text. example.com, site.io, and openai.ai still link.
    const suffix = host.slice(host.lastIndexOf('.') + 1);
    if (/^(md|sh)$/i.test(suffix)) {
      return null;
    }
  }
  return { href: match.href };
}

export type AutoLinkRange = {
  start: number;
  end: number;
  href: string;
};

/** Link spans inside one pasted block. Offsets are indexes into `text`. */
export function autoLinkRangesInText(text: string): AutoLinkRange[] {
  return find(text, { defaultProtocol: RICH_TEXT_AUTO_LINK_PROTOCOL })
    .filter(
      token => token.isLink && autoLinkMatch(token.value)?.href === token.href,
    )
    .map(token => ({
      start: token.start,
      end: token.end,
      href: token.href,
    }));
}

/**
 * Mark URLs inside text `insertText` just inserted.
 * `insertFrom` is the start of that text. Newlines stay in the text node,
 * so later lines use the same string offsets.
 */
export function addAutoLinkMarks(
  tr: Transaction,
  insertFrom: number,
  plain: string,
): void {
  const linkType = tr.doc.type.schema.marks.link;
  if (!linkType) {
    return;
  }
  const code = tr.doc.type.schema.marks.code;
  autoLinkRangesInText(plain).forEach(range => {
    const from = insertFrom + range.start;
    const to = insertFrom + range.end;
    if (from >= to || to > tr.doc.content.size) {
      return;
    }
    if (tr.doc.textBetween(from, to) !== plain.slice(range.start, range.end)) {
      return;
    }
    if (tr.doc.resolve(from).parent.type.spec.code) {
      return;
    }
    if (code && tr.doc.rangeHasMark(from, to, code)) {
      return;
    }
    if (tr.doc.rangeHasMark(from, to, linkType)) {
      return;
    }
    tr.addMark(from, to, linkType.create({ href: range.href }));
  });
}

function tokenAtOffset(
  text: string,
  offset: number,
): { start: number; end: number; value: string } | null {
  if (!text) {
    return null;
  }
  let index = offset;
  if (index > 0 && (index >= text.length || /\s/.test(text[index] ?? ''))) {
    index -= 1;
  }
  if (index < 0 || index >= text.length || /\s/.test(text[index] ?? '')) {
    return null;
  }
  let start = index;
  let end = index + 1;
  while (start > 0 && !/\s/.test(text[start - 1] ?? '')) {
    start -= 1;
  }
  while (end < text.length && !/\s/.test(text[end] ?? '')) {
    end += 1;
  }
  return { start, end, value: text.slice(start, end) };
}

function autoLinkSpanInToken(token: {
  start: number;
  end: number;
  value: string;
}): { start: number; end: number; value: string; href: string } | null {
  const match = find(token.value, {
    defaultProtocol: RICH_TEXT_AUTO_LINK_PROTOCOL,
  }).find(item => item.isLink && autoLinkMatch(item.value)?.href === item.href);
  if (!match) {
    return null;
  }
  const href = autoLinkMatch(match.value)?.href;
  if (!href) {
    return null;
  }
  return {
    start: token.start + match.start,
    end: token.start + match.end,
    value: match.value,
    href,
  };
}

/**
 * The text node under the caret. Hard breaks and images are boundaries, so
 * `textContent` cannot glue "hello" to a URL on the next line of the same block.
 */
function textNodeAtDocPos(
  parent: ProseMirrorNode,
  parentStart: number,
  docPos: number,
): { nodeStart: number; text: string; offset: number } | null {
  let pos = parentStart;
  let found: { nodeStart: number; text: string; offset: number } | null = null;
  parent.forEach(child => {
    if (found) {
      return;
    }
    const childEnd = pos + child.nodeSize;
    if (child.isText && docPos >= pos && docPos <= childEnd) {
      found = {
        nodeStart: pos,
        text: child.text ?? '',
        offset: Math.max(0, Math.min(child.text?.length ?? 0, docPos - pos)),
      };
      return;
    }
    pos = childEnd;
  });
  return found;
}

function caretTokenMark(
  state: EditorState,
  pos: number,
  linkType: MarkType,
): { from: number; to: number; href: string } | null {
  const $pos = state.doc.resolve(pos);
  if (!$pos.parent.isTextblock || $pos.parent.type.spec.code) {
    return null;
  }
  const textNode = textNodeAtDocPos($pos.parent, $pos.start(), pos);
  if (!textNode) {
    return null;
  }
  const token = tokenAtOffset(textNode.text, textNode.offset);
  const span = token ? autoLinkSpanInToken(token) : null;
  if (!span) {
    return null;
  }
  const from = textNode.nodeStart + span.start;
  const to = textNode.nodeStart + span.end;
  if (from >= to || state.doc.textBetween(from, to) !== span.value) {
    return null;
  }
  if (state.doc.rangeHasMark(from, to, linkType)) {
    return null;
  }
  const code = state.schema.marks.code;
  if (code && state.doc.rangeHasMark(from, to, code)) {
    return null;
  }
  return { from, to, href: span.href };
}

function transactionForCaretToken(
  state: EditorState,
  pos: number,
  linkType: MarkType,
): Transaction | null {
  const mark = caretTokenMark(state, pos, linkType);
  if (!mark) {
    return null;
  }
  return state.tr
    .addMark(mark.from, mark.to, linkType.create({ href: mark.href }))
    .setMeta('preventAutolink', true)
    .setMeta(AUTO_LINK_PLUGIN_KEY, true);
}

function caretLeaveAutoLink(type: MarkType): Plugin {
  return new Plugin({
    key: AUTO_LINK_PLUGIN_KEY,
    props: {
      handleDOMEvents: {
        blur(view) {
          if (!view.state.selection.empty) {
            return false;
          }
          const tr = transactionForCaretToken(
            view.state,
            view.state.selection.from,
            type,
          );
          if (tr) {
            view.dispatch(tr);
          }
          return false;
        },
      },
    },
    appendTransaction(transactions, oldState, newState) {
      if (
        transactions.some(
          tr =>
            tr.docChanged ||
            tr.getMeta('preventAutolink') ||
            tr.getMeta(AUTO_LINK_PLUGIN_KEY),
        )
      ) {
        return null;
      }
      if (
        !transactions.some(tr => tr.selectionSet) ||
        !oldState.selection.empty
      ) {
        return null;
      }
      const mark = caretTokenMark(oldState, oldState.selection.from, type);
      if (!mark) {
        return null;
      }
      const next = newState.selection.from;
      if (next >= mark.from && next <= mark.to) {
        return null;
      }
      return newState.tr
        .addMark(mark.from, mark.to, type.create({ href: mark.href }))
        .setMeta('preventAutolink', true)
        .setMeta(AUTO_LINK_PLUGIN_KEY, true);
    },
  });
}

const LINK_BLOCKING_MARKS = new Set(['code', 'highlight', 'textStyle']);

function splitTextNodeWithLinks(node: ProseMirrorNode): ProseMirrorNode[] {
  const text = node.text ?? '';
  if (!text || node.marks.some(mark => mark.type.name === 'link')) {
    return [node];
  }
  const ranges = autoLinkRangesInText(text);
  const linkType = node.type.schema.marks.link;
  if (!linkType || ranges.length === 0) {
    return [node];
  }
  const linkMarks = node.marks.filter(
    mark => !LINK_BLOCKING_MARKS.has(mark.type.name),
  );
  const parts: ProseMirrorNode[] = [];
  let cursor = 0;
  ranges.forEach(range => {
    if (range.start > cursor) {
      parts.push(
        node.type.schema.text(text.slice(cursor, range.start), node.marks),
      );
    }
    parts.push(
      node.type.schema.text(text.slice(range.start, range.end), [
        ...linkMarks,
        linkType.create({ href: range.href }),
      ]),
    );
    cursor = range.end;
  });
  if (cursor < text.length) {
    parts.push(node.type.schema.text(text.slice(cursor), node.marks));
  }
  return parts;
}

function linkUrlsInFragment(fragment: Fragment): Fragment {
  const children: ProseMirrorNode[] = [];
  fragment.forEach(node => {
    if (node.isText) {
      children.push(...splitTextNodeWithLinks(node));
      return;
    }
    children.push(linkUrlsInNode(node));
  });
  return Fragment.fromArray(children);
}

function linkUrlsInNode(node: ProseMirrorNode): ProseMirrorNode {
  if (node.isText || node.type.spec.code || node.childCount === 0) {
    return node;
  }
  return node.copy(linkUrlsInFragment(node.content));
}

type LinkSpan = {
  from: number;
  to: number;
  href: string;
  text: string;
};

function linkSpans(doc: ProseMirrorNode, linkType: MarkType): LinkSpan[] {
  const spans: LinkSpan[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText) {
      return;
    }
    const mark = node.marks.find(item => item.type === linkType);
    if (!mark || typeof mark.attrs.href !== 'string') {
      return;
    }
    const from = pos;
    const to = pos + node.nodeSize;
    const previous = spans[spans.length - 1];
    if (previous && previous.href === mark.attrs.href && previous.to === from) {
      previous.to = to;
      previous.text += node.text ?? '';
      return;
    }
    spans.push({
      from,
      to,
      href: mark.attrs.href,
      text: node.text ?? '',
    });
  });
  return spans;
}

function spanAt(spans: readonly LinkSpan[], pos: number): LinkSpan | null {
  return spans.find(span => pos >= span.from && pos <= span.to) ?? null;
}

/**
 * An auto-link's visible text is the URL. Editing that text has to update the
 * target, or drop the link once the text is no longer a URL. A toolbar link
 * whose label is not the URL is left alone.
 */
function syncEditedAutoLink(linkType: MarkType): Plugin {
  return new Plugin({
    key: SYNC_EDITED_AUTO_LINK_KEY,
    appendTransaction(transactions, oldState, newState) {
      if (
        !transactions.some(tr => tr.docChanged) ||
        oldState.doc.eq(newState.doc) ||
        transactions.some(tr => tr.getMeta(SYNC_EDITED_AUTO_LINK_KEY))
      ) {
        return null;
      }
      const spans = linkSpans(newState.doc, linkType);
      if (spans.length === 0) {
        return null;
      }
      const oldSpans = linkSpans(oldState.doc, linkType);
      const mapping = new Mapping();
      transactions.forEach(transaction => {
        mapping.appendMapping(transaction.mapping);
      });
      const tr = newState.tr;
      let changed = false;
      const code = newState.schema.marks.code;
      spans.forEach(span => {
        if (code && newState.doc.rangeHasMark(span.from, span.to, code)) {
          return;
        }
        const leading = span.text.length - span.text.trimStart().length;
        const trailing = span.text.length - span.text.trimEnd().length;
        const nextHref = autoLinkMatch(span.text.trim())?.href ?? null;
        if (nextHref === span.href) {
          if (leading > 0) {
            tr.removeMark(span.from, span.from + leading, linkType);
            changed = true;
          }
          if (trailing > 0) {
            tr.removeMark(span.to - trailing, span.to, linkType);
            changed = true;
          }
          return;
        }
        const oldPos = mapping.invert().map(span.from, -1);
        const oldSpan =
          spanAt(oldSpans, oldPos) ?? spanAt(oldSpans, oldPos + 1);
        if (!oldSpan || oldSpan.text === span.text) {
          return;
        }
        if (autoLinkMatch(oldSpan.text.trim())?.href !== oldSpan.href) {
          return;
        }
        const coreFrom = span.from + leading;
        const coreTo = span.to - trailing;
        tr.removeMark(span.from, span.to, linkType);
        if (nextHref && coreFrom < coreTo) {
          tr.addMark(coreFrom, coreTo, linkType.create({ href: nextHref }));
        }
        changed = true;
      });
      if (!changed) {
        return null;
      }
      return tr.setMeta(SYNC_EDITED_AUTO_LINK_KEY, true);
    },
  });
}

/** Turn pasted URL text into a link, including when the source formatted it as code. */
export function linkUrlsInSlice(slice: Slice): Slice {
  return new Slice(
    linkUrlsInFragment(slice.content),
    slice.openStart,
    slice.openEnd,
  );
}

function pastedUrlLink(): Plugin {
  return new Plugin({
    key: new PluginKey('siteScopePastedUrlLink'),
    props: {
      transformPasted: slice => linkUrlsInSlice(slice),
    },
  });
}

/** Web and native rich text share this link mark (auto-link + toolbar). */
export const SiteScopeLink = Link.extend({
  addOptions() {
    const parentOptions = this.parent?.();
    if (!parentOptions) {
      throw new Error('SiteScopeLink requires the link extension');
    }
    return {
      ...parentOptions,
      openOnClick: true,
      autolink: true,
      linkOnPaste: true,
      defaultProtocol: RICH_TEXT_AUTO_LINK_PROTOCOL,
      HTMLAttributes: {
        rel: 'noopener noreferrer',
        target: '_blank',
        style: `color: ${RICH_HTML_LINK_COLOR}; text-decoration: underline; cursor: pointer;`,
      },
      shouldAutoLink: (url: string) => autoLinkMatch(url) !== null,
    };
  },
  addProseMirrorPlugins() {
    return [
      ...(this.parent?.() ?? []),
      syncEditedAutoLink(this.type),
      caretLeaveAutoLink(this.type),
      pastedUrlLink(),
    ];
  },
});

/**
 * Replace TenTap's stock link bridge with {@link SiteScopeLink}.
 * Throws when the kit has no `link` bridge, so native cannot silently keep
 * the stock extension.
 */
export function withSiteScopeAutoLink<T>(bridges: readonly T[]): T[] {
  let replaced = 0;
  const next = bridges.map(bridge => {
    if (
      !bridge ||
      typeof bridge !== 'object' ||
      !('name' in bridge) ||
      bridge.name !== 'link' ||
      !('clone' in bridge) ||
      typeof bridge.clone !== 'function'
    ) {
      return bridge;
    }
    const linkBridge = bridge.clone() as T & {
      tiptapExtension?: unknown;
    };
    linkBridge.tiptapExtension = SiteScopeLink;
    replaced += 1;
    return linkBridge;
  });
  if (replaced === 0) {
    throw new Error(
      'SiteScopeLink was not installed on the native link bridge',
    );
  }
  return next;
}

export type EditorTextRange = { from: number; to: number };

/** Apply `href` to a saved range, or insert the address when nothing is selected. */
export function linkEditorRange(
  editor: Editor,
  href: string,
  range: EditorTextRange | null,
): boolean {
  if (/[<>]/.test(href)) {
    return false;
  }
  const focus = { scrollIntoView: false } as const;
  if (range && range.to > range.from) {
    return editor
      .chain()
      .focus(undefined, focus)
      .setTextSelection(range)
      .unsetLink()
      .setLink({ href })
      .run();
  }
  if (editor.isActive('link')) {
    return editor
      .chain()
      .focus(undefined, focus)
      .extendMarkRange('link')
      .setLink({ href })
      .run();
  }
  const from = editor.state.selection.from;
  const inserted = editor
    .chain()
    .focus(undefined, focus)
    .command(({ tr }) => {
      tr.insertText(href, from);
      return true;
    })
    .run();
  if (!inserted) {
    return false;
  }
  const to = editor.state.selection.from;
  const linked = editor
    .chain()
    .focus(undefined, focus)
    .setTextSelection({ from, to })
    .setLink({ href })
    .run();
  if (!linked) {
    editor
      .chain()
      .focus(undefined, focus)
      .setTextSelection({ from, to })
      .deleteSelection()
      .run();
    return false;
  }
  return true;
}
